import { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';

// Valida un destino interno relativo y seguro (mismo origen, una sola "/" inicial,
// sin esquema ni protocolo). Rechaza rutas del flujo de autenticación para evitar
// bucles (login, signup, callback, etc.), excepto la recuperación que se maneja aparte.
function isValidInternalPath(path: string | null): boolean {
  if (!path) return false;
  if (!path.startsWith('/')) return false;
  if (path.startsWith('//')) return false;
  if (path.includes('://')) return false;

  const lower = path.toLowerCase();
  const blocked = [
    '/login',
    '/auth/login',
    '/auth/forgot-password',
    '/auth/callback',
  ];
  return !blocked.some((b) => lower === b || lower.startsWith(`${b}/`));
}

export default function AuthCallbackPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;

    async function run() {
      const code = searchParams.get('code');

      if (!code) {
        if (active) setError('No se recibió el código de verificación.');
        return;
      }
      if (!isSupabaseConfigured || !supabase) {
        if (active) setError('La conexión con Supabase no está configurada.');
        return;
      }

      const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);

      if (exchangeError) {
        if (active) setError(exchangeError.message);
        return;
      }

      const next = searchParams.get('next');

      // Recuperación de contraseña: siempre a la página de nueva contraseña.
      if (next === '/auth/reset-password') {
        if (active) navigate('/auth/reset-password', { replace: true });
        return;
      }

      const destination = isValidInternalPath(next) ? next : '/admin';
      if (active) navigate(destination, { replace: true });
    }

    run();

    return () => {
      active = false;
    };
  }, [searchParams, navigate]);

  if (error) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
        <div className="w-full max-w-md bg-white rounded-2xl border border-background-200 p-8 text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
            <i className="ri-error-warning-line text-red-600 text-xl"></i>
          </div>
          <h1 className="text-lg font-semibold text-foreground-900 mb-2">No se pudo completar la verificación</h1>
          <p className="text-sm text-foreground-500 mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-lg bg-accent-500 text-white hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Volver al inicio de sesión
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background-50 gap-4">
      <div className="w-9 h-9 rounded-full border-2 border-accent-300 border-t-accent-500 animate-spin"></div>
      <p className="text-sm text-foreground-500">Verificando tu sesión…</p>
    </div>
  );
}